import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { shopsAPI, shopSession } from "../services/api";

/**
 * The shop this dashboard is open on, and what the signed-in member may do
 * in it — read once, shared by the sidebar, the route guards and the pages.
 *
 * `can(permission)` is the one question anything here asks. The server sends
 * an owner's permissions spelled out in full, so nothing needs to special-case
 * the role except the few areas no permission reaches (OWNER_ONLY).
 *
 * This decides what is offered, never what is allowed: the API enforces every
 * permission itself. Hiding what someone cannot do only spares them a screen
 * of refusals.
 */
export const OWNER_ONLY = "owner";

const MembershipContext = createContext(null);

export const MembershipProvider = ({ children }) => {
  const [state, setState] = useState({
    loading: true,
    shop: null,
    membership: null,
    subscription: null,
    support: null,
    flags: null,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await shopsAPI.current();
      // Which shop this install's token is for, so switching can come back to it
      shopSession.record(data.shop?.id);
      setState({
        loading: false,
        shop: data.shop,
        membership: data.membership,
        subscription: data.subscription,
        support: data.support,
        flags: data.flags,
      });
    } catch {
      setState((current) => ({ ...current, loading: false }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { membership } = state;
  const isOwner = membership?.role === "owner";

  const can = useCallback(
    (permission) => {
      if (!membership) return false;
      if (permission === OWNER_ONLY) return membership.role === "owner";
      return (membership.permissions || []).includes(permission);
    },
    [membership],
  );

  /**
   * Whether the shop's plan includes a module. Opt-out, like the server's
   * requireModule: only an explicit false turns one off.
   */
  const moduleOn = useCallback(
    (key) => state.shop?.enabled_modules?.[key] !== false,
    [state.shop],
  );

  /**
   * Whether this shop has been given a new feature yet (release flags, set by the
   * operator). Opt-in, unlike modules: anything not explicitly on is off, so an
   * older server that sends no flags shows nothing new.
   */
  const flag = useCallback((key) => state.flags?.[key] === true, [state.flags]);

  /** A screen is offered when it is this member's, the shop's plan has it, and its flag is on. */
  const isOffered = useCallback(
    (item) =>
      can(item.permission) &&
      (!item.module || moduleOn(item.module)) &&
      (!item.flag || flag(item.flag)),
    [can, moduleOn, flag],
  );

  const setShop = useCallback(
    (shop) => setState((current) => ({ ...current, shop })),
    [],
  );

  return (
    <MembershipContext.Provider
      value={{ ...state, isOwner, can, moduleOn, flag, isOffered, setShop, refresh: load }}
    >
      {children}
    </MembershipContext.Provider>
  );
};

export const useMembership = () => useContext(MembershipContext);
